/**
 * The board as a GRAPH, not a 2D array.
 *
 * This is the architectural decision the whole project rests on. Obstacles come
 * in three forms and all three are free in this representation:
 *
 *   hole    removes a node
 *   wall    removes an edge between two otherwise-adjacent nodes
 *   outline is just a particular arrangement of holes
 *
 * Adjacency is stored as a flat Int32Array with a stride of 4 (up, right, down,
 * left) holding -1 for "no neighbour". Flat typed arrays rather than a Map of
 * arrays because the solver walks this structure millions of times.
 */

import type { Edge, NodeId } from "./types.ts";

/** Direction deltas, indexed 0..3 to match the adjacency stride. */
export const DIRS: ReadonlyArray<{ dr: number; dc: number; name: string }> = [
  { dr: -1, dc: 0, name: "up" },
  { dr: 0, dc: 1, name: "right" },
  { dr: 1, dc: 0, name: "down" },
  { dr: 0, dc: -1, name: "left" },
];

export interface Board {
  rows: number;
  cols: number;
  /** rows * cols — the id space, including holes. */
  size: number;
  /** 1 if the cell is playable, 0 if it is a hole. */
  present: Uint8Array;
  /** size * 4 neighbour ids, -1 where there is no edge. */
  nbr: Int32Array;
  /** Number of edges at each node. 0 for holes. */
  deg: Int32Array;
  /** Checkerboard colour, (r + c) & 1. The parity pruning depends on this. */
  color: Uint8Array;
  /** Ids of playable cells, ascending. */
  playable: NodeId[];
  /** Playable cells of each colour. */
  colorCount: [number, number];
}

export function idOf(cols: number, r: number, c: number): NodeId {
  return r * cols + c;
}

export function rowOf(cols: number, id: NodeId): number {
  return Math.floor(id / cols);
}

export function colOf(cols: number, id: NodeId): number {
  return id % cols;
}

/** Canonical edge ordering, so an edge has one representation everywhere. */
export function edge(a: NodeId, b: NodeId): Edge {
  return a < b ? [a, b] : [b, a];
}

export function edgeKey(a: NodeId, b: NodeId): number {
  return a < b ? a * 4096 + b : b * 4096 + a;
}

export function createBoard(
  rows: number,
  cols: number,
  holes: Iterable<NodeId> = [],
  walls: ReadonlyArray<readonly [NodeId, NodeId]> = [],
): Board {
  const size = rows * cols;
  const present = new Uint8Array(size).fill(1);
  for (const h of holes) {
    if (h < 0 || h >= size) throw new Error(`hole id ${h} out of range`);
    present[h] = 0;
  }

  const nbr = new Int32Array(size * 4).fill(-1);
  const deg = new Int32Array(size);
  const color = new Uint8Array(size);

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const id = idOf(cols, r, c);
      color[id] = (r + c) & 1;
      if (!present[id]) continue;
      for (let d = 0; d < 4; d++) {
        const nr = r + DIRS[d].dr;
        const nc = c + DIRS[d].dc;
        if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
        const nid = idOf(cols, nr, nc);
        if (!present[nid]) continue;
        nbr[id * 4 + d] = nid;
      }
    }
  }

  // Walls remove edges in both directions.
  for (const [a, b] of walls) {
    removeEdge(nbr, a, b);
    removeEdge(nbr, b, a);
  }

  const playable: NodeId[] = [];
  const colorCount: [number, number] = [0, 0];
  for (let id = 0; id < size; id++) {
    if (!present[id]) continue;
    playable.push(id);
    colorCount[color[id]]++;
    let d = 0;
    for (let k = 0; k < 4; k++) if (nbr[id * 4 + k] >= 0) d++;
    deg[id] = d;
  }

  return { rows, cols, size, present, nbr, deg, color, playable, colorCount };
}

function removeEdge(nbr: Int32Array, from: NodeId, to: NodeId): void {
  for (let d = 0; d < 4; d++) {
    if (nbr[from * 4 + d] === to) {
      nbr[from * 4 + d] = -1;
      return;
    }
  }
}

/** Neighbour ids of a node, as a plain array. Convenience, not hot-path. */
export function neighborsOf(board: Board, id: NodeId): NodeId[] {
  const out: NodeId[] = [];
  for (let d = 0; d < 4; d++) {
    const n = board.nbr[id * 4 + d];
    if (n >= 0) out.push(n);
  }
  return out;
}

/** Every edge in the board, each listed once. */
export function allEdges(board: Board): Edge[] {
  const out: Edge[] = [];
  for (const id of board.playable) {
    for (let d = 0; d < 4; d++) {
      const n = board.nbr[id * 4 + d];
      if (n > id) out.push([id, n]);
    }
  }
  return out;
}

/** Are all playable cells reachable from one another? */
export function isConnected(board: Board): boolean {
  const total = board.playable.length;
  if (total === 0) return false;
  const seen = new Uint8Array(board.size);
  const stack: NodeId[] = [board.playable[0]];
  seen[board.playable[0]] = 1;
  let count = 1;
  while (stack.length) {
    const u = stack.pop()!;
    for (let d = 0; d < 4; d++) {
      const v = board.nbr[u * 4 + d];
      if (v >= 0 && !seen[v]) {
        seen[v] = 1;
        count++;
        stack.push(v);
      }
    }
  }
  return count === total;
}

/**
 * A grid graph is bipartite, so a Hamiltonian path can only exist when the two
 * colour classes differ in size by at most one. This is the cheapest and most
 * effective early rejection there is — it kills bad hole sets before any search
 * starts, and prunes the solver mid-search.
 */
export function colorBalanceOk(board: Board): boolean {
  return Math.abs(board.colorCount[0] - board.colorCount[1]) <= 1;
}

/**
 * A degree-1 cell can only ever be an endpoint of the path. There are exactly
 * two endpoints, so more than two such cells makes the level unsolvable.
 */
export function degreeOneNodes(board: Board): NodeId[] {
  return board.playable.filter((id) => board.deg[id] === 1);
}
